import { collection, doc, getDoc, getDocs } from "firebase/firestore/lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { db } from "../auth/firebase";
import { useGlobalState } from "../globalState/GlobalState";
import i18n from "./i18n";

async function assetExists(url?: string) {
  if (!url) return false;
  try {
    const res = await fetch(url, { method: "HEAD" });
    return res.ok;
  } catch {
    return false;
  }
}

const getSizes = async (
  url?: string,
  fileName?: string,
  subfolder?: string,
  sizes?: number[],
) => {
  if (!url || !fileName || !subfolder || !sizes)
    return await Promise.resolve([]);
  return Promise.all(
    sizes.map(async (size) => {
      const newUrl = url?.replace(
        fileName,
        `${subfolder}%2F${fileName}_${size}x${size}`,
      );
      if (!(await assetExists(newUrl))) return;
      return {
        size: size / 2,
        url: newUrl,
      };
    }),
  );
};
export const useGetFireStoreSizes = (
  url?: string,
  placeholderSize = 128,
  sizes = [32, 64, 128, 320, 640, 768, 1024, 1280, 1536, 1920, 2560],
  subfolder = "resized",
) => {
  const [error, setError] = useState(false);

  const fileName = useMemo(() => {
    if (!url) return "";
    return decodeURIComponent(new URL(url).pathname).split("/").pop() || "";
  }, [url]);

  const [sources, setSources] = useState<{ size: number; url: string }[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!fileName) return;
    getSizes(url, fileName, subfolder, sizes).then((e) => {
      setSources(e.filter((e) => e !== undefined));
    });
  }, [url, fileName, subfolder]);

  const srcSet = useMemo(() => {
    return sources.map(({ url, size }) => `${url} ${size}w`).join(", ");
  }, [sources]);

  useEffect(() => {
    if (!sources.length || !sources[0].url) return;

    const image = new Image();
    image.onload = () => setError(false);
    image.onerror = () => setError(true);
    image.src = sources[0].url;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [sources]);

  if (!url)
    return {
      placeholderSrc: "",
      src: "",
      srcSet: "",
      sizes: "",
      error: true,
    };

  return {
    placeholderSrc:
      sources.find((img) => img.size === placeholderSize)?.url || url,
    src: url,
    srcSet: error ? "" : srcSet,
    error,
  };
};

export const useGetTranslations = (project) => {
  const [_, setTranslation] = useGlobalState().translationsState;

  const checkProjectHasTranslations = useCallback(async () => {
    if (!project?.organizationId || !project?.projectId) return false;
    // translations is a collection, so we need to check if it has any documents
    const translationCollectionPath = `organizations/${project.organizationId}/projects/${project.projectId}/translations`;
    // Use getDocs to check if there is at least one document in the collection
    const translationCollectionRef = collection(db, translationCollectionPath);
    const snapshot = await getDocs(translationCollectionRef);
    return !snapshot.empty;
  }, [project?.organizationId, project?.projectId]);

  const fetchTranslations = async (lang) => {
    const translationJsonPath = `organizations/${project?.organizationId}/projects/${project?.projectId}/translations/${lang}`;
    const translationJsonDoc = await getDoc(doc(db, translationJsonPath));
    if (!translationJsonDoc.exists()) return;
    const translationJson = translationJsonDoc.data();

    const { translations, originalToHashedMap } = translationJson;
    const unhashedTranslations = {};

    // List of keys to filter out when language ends with -simple
    // const keysToFilter = [
    //   project?.title,
    //   // Add all titles from project.content
    //   ...(project?.content?.map(item => item.title) || [])
    // ].filter(Boolean);

    for (const [hashedKey, value] of Object.entries(translations)) {
      const originalKey = originalToHashedMap?.[hashedKey] || hashedKey;
      // Skip if language ends with -simple and key is in the filter list
      // if (lang.endsWith("-simple") && keysToFilter.includes(originalKey)) {
      //   continue;
      // }
      unhashedTranslations[originalKey] = value;
    }
    console.log(translationJson);
    console.log(unhashedTranslations);
    setTranslation(unhashedTranslations);
  };

  return { fetchTranslations, checkProjectHasTranslations };
};

export const useDimensions = <T extends HTMLElement>() => {
  const containerRef = useRef<T>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observeTarget = containerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (observeTarget) {
      resizeObserver.observe(observeTarget);
    }

    return () => {
      if (observeTarget) {
        resizeObserver.unobserve(observeTarget);
      }
    };
  }, []);
  return [containerRef, dimensions] as const;
};

const quotes = {
  ar: ["«", "»"], // Arabic
  cs: ["„", "“"], // Czech
  de: ["„", "“"], // German
  en: ["“", "”"], // English
  es: ["«", "»"], // Spanish
  fr: ["«\u00A0", "\u00A0»"], // French with non-breaking space
  it: ["«", "»"], // Italian
  nl: ["“", "”"], // Dutch (typically same as English)
  pl: ["„", "”"], // Polish
  ru: ["«", "»"], // Russian
  tr: ["“", "”"], // Turkish
  uk: ["«", "»"], // Ukrainian
};
export const useLocalizeQuotes = (locale: string) => {
  const localizedQuote = useCallback(
    (text: string) => {
      const [open, close] = quotes[locale as keyof typeof quotes] || ['"', '"'];
      return `${open}${text}${close}`;
    },
    [locale],
  );

  return localizedQuote;
};
